import { CustomerHistoryPanel } from "./history";
import { TpvDialog } from "../../../../components/TpvDialog";

import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import "./style.css";

import {
  changeOrderShippingAddresses,
  updateClient,
  setAlert,
} from "../../../../reducers/userConfigSlice";
import { GrRadialSelected } from "react-icons/gr";
import { translateMessage } from "@delibrandev/utils";
import {
  createCustomerAddress,
  deleteCustomerAddress,
  getCustomerAddresses,
  updateCustomerAddress,
} from "../../../../api/cart";

const EMPTY_CLIENT = {};

const AddressInput = ({ id, label, defaultValue = "" }) => (
  <label className="delivery-address-field" htmlFor={id}>
    <span>{label}</span>
    <input
      id={id}
      name={id}
      type={id === "phone" ? "tel" : "text"}
      defaultValue={defaultValue}
    />
  </label>
);

const safeElValue = (id) => {
  const el = document.getElementById(id);
  return el ? el.value || "" : "";
};

const mergeAddresses = (savedAddresses, existingAddresses) => {
  const unique = new Map();

  [...savedAddresses, ...existingAddresses].forEach((address) => {
    const id = address?.addressId;
    if (id && !unique.has(String(id))) unique.set(String(id), address);
  });

  return Array.from(unique.values());
};

const readAddressForm = () => ({
  name: safeElValue("name"),
  phone: safeElValue("phone"),
  street: safeElValue("street"),
  number: safeElValue("number"),
  floor: safeElValue("floor"),
  door: safeElValue("door"),
  block: safeElValue("block"),
  city: safeElValue("city"),
  state: safeElValue("state"),
  country: safeElValue("country") || "España",
  zipCode: safeElValue("cp"),
});

export const DeliveryOptions = ({
  clientData,
  setShowFloatPanel,
  setDefaultClient,
}) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const saveInFlight = useRef(false);
  const [addressPanel, changeAddressPanel] = useState("clientAddress");
  const [editingAddress, setEditingAddress] = useState(null);
  const [savedAddressIds, setSavedAddressIds] = useState(() => new Set());
  const [section, setSection] = useState("addresses");
  const [addressesLoading, setAddressesLoading] = useState(false);

  const client =
    useSelector((state) => state.aplicationConfig.userConfig.client) ||
    EMPTY_CLIENT;
  const clientRef = useRef(client);
  const clientAddresses =
    useSelector(
      (state) => state.aplicationConfig.userConfig.client?.addresses,
    ) || null;

  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  const deliverys = useMemo(() => {
    if (Array.isArray(clientAddresses?.shippingAddresses)) {
      return clientAddresses.shippingAddresses;
    }

    if (Array.isArray(clientAddresses?.addresses)) {
      return clientAddresses.addresses;
    }

    return [];
  }, [clientAddresses]);

  // El orden visual se conserva durante toda la vida del modal aunque Redux
  // reordene los arrays para colocar el elemento seleccionado en primera posición.
  const [addressOrder, setAddressOrder] = useState(() =>
    deliverys.map((address) => address?.addressId).filter(Boolean),
  );

  // La selección visual tampoco depende de la posición 0 de Redux.
  const [selectedAddressId, setSelectedAddressId] = useState(
    () => deliverys?.[0]?.addressId || null,
  );

  // Si llegan nuevos elementos desde Redux, se añaden al orden visual existente
  // sin recolocar los que ya estaban pintados en el modal.
  useEffect(() => {
    const currentIds = deliverys
      .map((address) => address?.addressId)
      .filter(Boolean);

    setAddressOrder((previousOrder) => {
      const existingIds = previousOrder.filter((id) => currentIds.includes(id));
      const newIds = currentIds.filter((id) => !existingIds.includes(id));
      return [...existingIds, ...newIds];
    });

    if (!selectedAddressId && currentIds.length > 0) {
      setSelectedAddressId(currentIds[0]);
    }
  }, [deliverys, selectedAddressId]);

  const visibleDeliverys = useMemo(() => {
    const deliveryMap = new Map(
      deliverys.map((address) => [address?.addressId, address]),
    );
    return addressOrder.map((id) => deliveryMap.get(id)).filter(Boolean);
  }, [deliverys, addressOrder]);

  const handleOrderAddress = useCallback(
    (id) => {
      if (saveInFlight.current || !id) return;

      setSelectedAddressId(id);
      dispatch(changeOrderShippingAddresses(id));
    },
    [dispatch],
  );

  const refreshSavedAddresses = useCallback(
    async (addressIdToSelect = null, removedId = null) => {
      if (!clientData?.customerId) return [];

      setAddressesLoading(true);
      const response = await getCustomerAddresses(clientData.customerId);
      setAddressesLoading(false);
      if (!response || response.error) {
        setSaveError(
          response?.message ||
            "No se han podido cargar las direcciones. Reabre la ficha para reintentar.",
        );
        return [];
      }

      const savedAddresses = Array.isArray(response?.data) ? response.data : [];
      const currentClient = clientRef.current;
      if (String(currentClient?.customerId) !== String(clientData.customerId))
        return [];

      const existingAddresses = Array.isArray(
        currentClient?.addresses?.shippingAddresses,
      )
        ? currentClient.addresses.shippingAddresses
        : Array.isArray(currentClient?.addresses?.addresses)
          ? currentClient.addresses.addresses
          : [];
      const addresses = mergeAddresses(
        savedAddresses,
        existingAddresses.filter((a) => a.addressId !== removedId),
      );

      setSavedAddressIds(
        new Set(savedAddresses.map((address) => String(address.addressId))),
      );
      dispatch(
        updateClient({
          ...currentClient,
          addresses: {
            ...currentClient.addresses,
            shippingAddresses: addresses,
          },
        }),
      );

      if (addressIdToSelect) {
        setSelectedAddressId(addressIdToSelect);
        dispatch(changeOrderShippingAddresses(addressIdToSelect));
      }

      return addresses;
    },
    [clientData?.customerId, dispatch],
  );

  useEffect(() => {
    refreshSavedAddresses();
  }, [refreshSavedAddresses]);

  const saveDelivery = useCallback(async () => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    setSaving(true);
    setSaveError("");
    try {
      if (!clientData?.customerId) throw Error("Cliente no válido");
      const newAddress = readAddressForm();
      if (
        !newAddress.street.trim() ||
        !newAddress.city.trim() ||
        !newAddress.zipCode.trim()
      )
        throw Error("Completa calle, ciudad y código postal");
      const response = editingAddress?.addressId
        ? await updateCustomerAddress(
            clientData.customerId,
            editingAddress.addressId,
            newAddress,
          )
        : await createCustomerAddress(clientData.customerId, newAddress);
      const addressId =
        response?.data?.addressId ?? response?.data?.address?.addressId;
      if (response?.error !== false || !addressId)
        throw Error(
          response?.message ||
            "No se confirmó el guardado. Revisa las direcciones antes de repetir.",
        );
      const addresses = await refreshSavedAddresses(addressId);
      setEditingAddress(null);
      changeAddressPanel("clientAddress");
      if (
        !addresses.some(
          (address) => String(address.addressId) === String(addressId),
        )
      )
        setSaveError(
          "La dirección se ha guardado, pero no se pudo actualizar la lista. Reabre la ficha antes de volver a añadirla.",
        );
      else
        dispatch(
          setAlert(
            translateMessage(
              editingAddress ? "Dirección actualizada" : "Dirección añadida",
            ),
          ),
        );
    } catch (error) {
      setSaveError(
        error.message ||
          "No se pudo guardar la dirección. Comprueba la conexión y revisa la lista antes de repetir.",
      );
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  }, [clientData?.customerId, dispatch, editingAddress, refreshSavedAddresses]);

  const removeDelivery = useCallback(
    async (address) => {
      if (
        !address?.addressId ||
        !clientData?.customerId ||
        !savedAddressIds.has(String(address.addressId))
      )
        return;
      if (!window.confirm("¿Quieres eliminar esta dirección guardada?")) return;

      try {
        const response = await deleteCustomerAddress(
          clientData.customerId,
          address.addressId,
        );
        if (!response || response.error) {
          dispatch(
            setAlert(
              translateMessage(
                response?.message || "No se ha podido eliminar la dirección",
              ),
            ),
          );
          return;
        }

        const addresses = await refreshSavedAddresses(null, address.addressId);
        if (selectedAddressId === address.addressId) {
          const nextAddressId = addresses[0]?.addressId ?? null;
          setSelectedAddressId(nextAddressId);
          if (nextAddressId)
            dispatch(changeOrderShippingAddresses(nextAddressId));
        }
        dispatch(setAlert(translateMessage("Dirección eliminada")));
      } catch (error) {
        console.error("[DeliveryOptions] Error en removeDelivery:", error);
        dispatch(setAlert(translateMessage("Error al eliminar la dirección")));
      }
    },
    [
      clientData?.customerId,
      dispatch,
      refreshSavedAddresses,
      savedAddressIds,
      selectedAddressId,
    ],
  );

  const NewAddress = ({ data }) => (
    <form
      className="delivery-new-address"
      onSubmit={(event) => {
        event.preventDefault();
        saveDelivery();
      }}
    >
      <fieldset disabled={saving}>
        <div className="delivery-address-fields">
          {[
            ["name", "Nombre del cliente", data?.name ?? clientData?.fullname ?? ""],
            ["phone", "Teléfono", data?.phone ?? clientData?.phone ?? ""],
            ["street", "Calle", data?.street || ""],
            ["number", "Número", data?.number || ""],
            ["floor", "Piso", data?.floor || ""],
            ["door", "Puerta", data?.door || ""],
            ["block", "Bloque", data?.block || ""],
            ["cp", "Cód. Postal", data?.zipCode || ""],
            ["city", "Ciudad", data?.city || ""],
            ["state", "Provincia", data?.state || ""],
            ["country", "País", data?.country || "España"],
          ].map(([id, label, defaultValue]) => (
            <div className={`delivery-field-${id}`} key={id}>
              <AddressInput id={id} label={label} defaultValue={defaultValue} />
            </div>
          ))}
        </div>
        <button type="submit" className="delivery-save" disabled={saving}>
          {saving ? "Guardando…" : editingAddress ? "Guardar cambios" : "Añadir dirección"}
        </button>
      </fieldset>
    </form>
  );

  const ClientAddress = () => {
    if (!Array.isArray(visibleDeliverys) || visibleDeliverys.length === 0) {
      return (
        <div style={{ opacity: 0.8, padding: "10px 0" }}>
          El cliente no tiene direcciones recientes.
        </div>
      );
    }

    return (
      <div className="delivery-address-grid">
        {visibleDeliverys.map((address) => {
          const isSelected = selectedAddressId === address.addressId;
          const isSaved = savedAddressIds.has(String(address.addressId));

          return (
            <div
              key={address.addressId}
              className={`client-address ${isSelected ? "optionSelected" : ""}`}
            >
              <button
                type="button"
                className="deliveryAddressSelect"
                disabled={saving}
                aria-pressed={isSelected}
                onClick={() => handleOrderAddress(address.addressId)}
              >
                <div className="flex">
                  <div>
                    <b>{address?.name}</b>
                  </div>
                  <div>
                    {isSelected && (
                      <GrRadialSelected
                        color="var(--color-process)"
                        size="1rem"
                      />
                    )}
                  </div>
                </div>
                <div>
                  {[
                    address?.street,
                    address?.number,
                    address?.floor,
                    address?.door,
                    address?.block,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                <div>{`${address?.zipCode || ""} ${address?.city || ""} ${address?.state || ""}`}</div>
                <div>{address?.phone}</div>
              </button>
              {isSaved && (
                <div className="delivery-address-actions">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditingAddress(address);
                      changeAddressPanel("newAddress");
                    }}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="delivery-address-delete"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeDelivery(address);
                    }}
                  >
                    Eliminar
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const closePanelAddress = async () => {
    if (saveInFlight.current) return;
    saveInFlight.current = true;
    setSaving(true);
    setSaveError("");
    try {
      await setDefaultClient();
      setShowFloatPanel(false);
    } catch {
      setSaveError(
        "No se pudo cancelar y restituir el cliente contado. Vuelve a intentarlo.",
      );
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <TpvDialog
      title="Datos del cliente"
      className={`tpvDeliveryDialog ${section === "addresses" && addressPanel === "newAddress" ? "tpvDeliveryAddressEditor" : ""}`}
      busy={saving}
      onClose={() => setShowFloatPanel(false)}
    >
      <nav className="delivery-dialog-tabs" aria-label="Ficha del cliente">
        {[
          ["addresses", "Direcciones"],
          ["orders", "Pedidos anteriores"],
          ["carts", "Carritos anteriores"],
        ].map(([key, text]) => (
          <button
            key={key}
            type="button"
            aria-pressed={section === key}
            disabled={saving}
            onClick={() => {
              setSection(key);
              setSaveError("");
            }}
          >
            {text}
          </button>
        ))}
      </nav>
      <div className="tpvDialogBody delivery-dialog-body">
        {saveError && (
          <p role="alert" className="tpvDialogError">
            {saveError}
          </p>
        )}
        {section === "addresses" ? (
          <>
            <div className="delivery-address-heading">
              <div>
                <h3>Direcciones de {clientData?.fullname || "cliente"}</h3>
                <p>Elige dónde entregar el pedido.</p>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setSaveError("");
                  setEditingAddress(null);
                  changeAddressPanel(
                    addressPanel === "clientAddress"
                      ? "newAddress"
                      : "clientAddress",
                  );
                }}
              >
                {addressPanel === "clientAddress"
                  ? "Nueva dirección"
                  : "Volver"}
              </button>
            </div>
            {addressesLoading && <p role="status">Cargando direcciones…</p>}
            {addressPanel === "clientAddress"
              ? ClientAddress()
              : NewAddress({ data: editingAddress })}
          </>
        ) : (
          <CustomerHistoryPanel
            key={section}
            customerId={clientData.customerId}
            kind={section}
            address={visibleDeliverys.find(
              (a) => a.addressId === selectedAddressId,
            )}
            onAddress={() => setSection("addresses")}
            onRepeated={() => setShowFloatPanel(false)}
            onBusy={setSaving}
          />
        )}
      </div>
      <footer className="delivery-dialog-actions">
        <button type="button" disabled={saving} onClick={closePanelAddress}>
          Cancelar
        </button>
        {section === "addresses" && addressPanel === "clientAddress" && (
          <button
            type="button"
            className="tpvPrimary"
            disabled={
              saving ||
              !visibleDeliverys.some((a) => a.addressId === selectedAddressId)
            }
            onClick={() => setShowFloatPanel(false)}
          >
            Confirmar dirección
          </button>
        )}
      </footer>
    </TpvDialog>
  );
};
